/* eslint-disable no-param-reassign */
/* eslint-disable no-return-assign */

import { keysToSnakeCase, camelizeKeys } from "lib/helpers";
import { api } from "rdx/services/api";
import { showAlertHelper } from "rdx/utils/showAlertHelper";
import SchemaValidator from "util/schema-helpers/SchemaValidator";
import { isUndefined } from "types/guards";
import messages from "./messages";
import {
  GetRegionalOverridesResponseT,
  CreateRegionalOverrideParamsT,
  UpdateRegionalOverrideParamsT,
  MutateRegionalOverrideResponseT,
  GetRegionalOverridesParamsT,
  DeleteRegionalOverrideParamsT,
} from "./types";
import { GetRegionalOverridesResponseSchema, MutateRegionalOverrideResponseSchema } from "./schemas";
import { REGIONAL_POWURLINE_OVERRIDE_TAGS } from "./tags";

export const regionalPowurlineOverrideApi = api
  .enhanceEndpoints({ addTagTypes: [REGIONAL_POWURLINE_OVERRIDE_TAGS.REGIONAL_POWURLINE_OVERRIDES] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getRegionalOverrides: builder.query<GetRegionalOverridesResponseT["body"], GetRegionalOverridesParamsT>({
        query: ({ enterpriseId }) => ({
          url: `/enterprises/${enterpriseId}/regional_powurline_overrides`,
        }),
        transformResponse: (res: GetRegionalOverridesResponseT) => {
          SchemaValidator.validate<typeof GetRegionalOverridesResponseSchema>({
            schema: GetRegionalOverridesResponseSchema,
            schemaName: "GetRegionalOverridesResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(() => {
            showAlertHelper(dispatch, messages.ERROR_GETTING_REGIONAL_OVERRIDES, "error");
          });
        },
        providesTags: [REGIONAL_POWURLINE_OVERRIDE_TAGS.REGIONAL_POWURLINE_OVERRIDES],
      }),
      createRegionalOverride: builder.mutation<MutateRegionalOverrideResponseT["body"], CreateRegionalOverrideParamsT>({
        query: ({ enterpriseId, adderId, sellerPowurlinePpw, entBaselinePpw }) => ({
          url: `/enterprises/${enterpriseId}/regional_powurline_overrides`,
          method: "POST",
          body: keysToSnakeCase({
            adderId,
            sellerPowurlinePpw,
            ...(entBaselinePpw !== undefined && { entBaselinePpw }),
          }),
        }),
        transformResponse: (res: MutateRegionalOverrideResponseT) => {
          SchemaValidator.validate<typeof MutateRegionalOverrideResponseSchema>({
            schema: MutateRegionalOverrideResponseSchema,
            schemaName: "MutateRegionalOverrideResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        invalidatesTags: [REGIONAL_POWURLINE_OVERRIDE_TAGS.REGIONAL_POWURLINE_OVERRIDES],
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          showAlertHelper(dispatch, messages.CREATING_REGIONAL_OVERRIDE_IN_PROGRESS, "info");
          queryFulfilled
            .then(() => {
              showAlertHelper(dispatch, messages.CREATE_REGIONAL_OVERRIDE_SUCCESS, "success");
            })
            .catch(({ error }) => {
              showAlertHelper(
                dispatch,
                error.data.error?.message || messages.ERROR_CREATING_REGIONAL_OVERRIDE,
                "error",
              );
            });
        },
      }),
      updateRegionalOverride: builder.mutation<MutateRegionalOverrideResponseT["body"], UpdateRegionalOverrideParamsT>({
        query: ({ id, enterpriseId, sellerPowurlinePpw, entBaselinePpw }) => ({
          url: `/enterprises/${enterpriseId}/regional_powurline_overrides/${id}`,
          method: "PATCH",
          body: keysToSnakeCase({ sellerPowurlinePpw, ...(entBaselinePpw !== undefined && { entBaselinePpw }) }),
        }),
        transformResponse: (res: MutateRegionalOverrideResponseT) => {
          SchemaValidator.validate<typeof MutateRegionalOverrideResponseSchema>({
            schema: MutateRegionalOverrideResponseSchema,
            schemaName: "MutateRegionalOverrideResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: ({ id, enterpriseId }, { dispatch, queryFulfilled }) => {
          showAlertHelper(dispatch, messages.UPDATING_REGIONAL_OVERRIDE_IN_PROGRESS, "info");
          queryFulfilled
            .then(({ data }) => {
              dispatch(
                regionalPowurlineOverrideApi.util.updateQueryData(
                  "getRegionalOverrides",
                  { enterpriseId },
                  ({ overrides }) => {
                    const match = overrides.find((pb) => pb.id === id);
                    if (!isUndefined(match)) {
                      match.sellerPowurlinePpw = data.sellerPowurlinePpw;
                      if (!isUndefined(data.entBaselinePpw)) {
                        match.entBaselinePpw = data.entBaselinePpw;
                      }
                    }
                  },
                ),
              );
              showAlertHelper(dispatch, messages.UPDATE_REGIONAL_OVERRIDE_SUCCESS, "success");
            })
            .catch(({ error }) => {
              showAlertHelper(
                dispatch,
                error.data.error?.message || messages.ERROR_UPDATING_REGIONAL_OVERRIDE,
                "error",
              );
            });
        },
      }),
      deleteRegionalOverride: builder.mutation<MutateRegionalOverrideResponseT["body"], DeleteRegionalOverrideParamsT>({
        query: ({ id, enterpriseId }) => ({
          url: `/enterprises/${enterpriseId}/regional_powurline_overrides/${id}`,
          method: "DELETE",
        }),
        transformResponse: (res: MutateRegionalOverrideResponseT) => {
          SchemaValidator.validate<typeof MutateRegionalOverrideResponseSchema>({
            schema: MutateRegionalOverrideResponseSchema,
            schemaName: "MutateRegionalOverrideResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        invalidatesTags: [REGIONAL_POWURLINE_OVERRIDE_TAGS.REGIONAL_POWURLINE_OVERRIDES],
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          showAlertHelper(dispatch, messages.DELETING_REGIONAL_OVERRIDE_IN_PROGRESS, "info");
          queryFulfilled
            .then(() => {
              showAlertHelper(dispatch, messages.DELETE_REGIONAL_OVERRIDE_SUCCESS, "success");
            })
            .catch(({ error }) => {
              showAlertHelper(
                dispatch,
                error.data.error?.message || messages.ERROR_DELETING_REGIONAL_OVERRIDE,
                "error",
              );
            });
        },
      }),
    }),
  });

export const {
  useGetRegionalOverridesQuery,
  useCreateRegionalOverrideMutation,
  useUpdateRegionalOverrideMutation,
  useDeleteRegionalOverrideMutation,
} = regionalPowurlineOverrideApi;
